import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Alert,
  Form,
  Modal,
  Spinner,
  Dropdown,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaStar,
  FaFilter,
  FaSearch,
  FaChartLine
} from 'react-icons/fa';
import ScoringRuleApi from '../../../api/ScoringRuleApi';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import { getEntitlementsSection } from '../../../utils/entitlements';
import ScoringRuleForm from './ScoringRuleForm';
import './css/ScoringRuleManager.css';

const ScoringRuleManager = () => {
  const navigate = useNavigate();
  const scoringRuleEnt = getEntitlementsSection('scoringRule', {});
  const allowViewScoringRule = scoringRuleEnt.allowViewScoringRule !== false;
  const allowCreateScoringRule = scoringRuleEnt.allowCreateScoringRule !== false;
  // null = entitlements not set (backward compat: no restriction); [] = no objects allowed
  const allowedObjectsList = Array.isArray(scoringRuleEnt.allowedObjects) ? scoringRuleEnt.allowedObjects : null;
  const allowedObjectsSet = useMemo(
    () => (allowedObjectsList === null ? null : new Set(allowedObjectsList)),
    [allowedObjectsList]
  );

  const [rules, setRules] = useState([]);
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all'); // all, active, inactive
  const [filterObject, setFilterObject] = useState('all'); // all or specific object
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);

  // Restrict to allowed objects only (for dropdown and form). null = no restriction; empty set = none allowed
  const visibleObjects = useMemo(() => {
    if (allowedObjectsSet === null) return objects;
    return objects.filter((o) => allowedObjectsSet.has(o.name));
  }, [objects, allowedObjectsSet]);

  // Rules the user is allowed to see (by object)
  const rulesForAllowedObjects = useMemo(() => {
    if (allowedObjectsSet === null) return rules;
    return rules.filter((r) => allowedObjectsSet.has(r.object_name));
  }, [rules, allowedObjectsSet]);

  useEffect(() => {
    if (!allowViewScoringRule) {
      navigate('/setup', { replace: true });
      return;
    }
    fetchObjects();
    fetchAllRules();
  }, [allowViewScoringRule, navigate]);

  const fetchObjects = async () => {
    try {
      const response = await ObjectConfigApi.fetchObjectConfigs();
      if (response?.success) {
        const objectList = response.data
          .filter(obj => !obj.is_deleted)
          .map(obj => ({
            name: obj.object_name,
            label: obj.object_label || obj.object_name,
            id: obj.id
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setObjects(objectList);
      }
    } catch (err) {
      console.error('Error fetching objects:', err);
      setError('Failed to fetch objects');
    }
  };

  const fetchAllRules = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('Fetching all scoring rules...');
      const scoringRuleApi = new ScoringRuleApi();
      const response = await scoringRuleApi.fetchAll();
      console.log('Fetch all rules - Full response:', response);
      console.log('Response data:', response?.data);
      console.log('Response success:', response?.data?.success);
      console.log('Response data array:', response?.data?.data);

      if (response?.data?.success) {
        const rulesData = response.data.data || [];
        console.log(`Successfully fetched ${rulesData.length} rules`);
        setRules(rulesData);
      } else {
        const errorMsg = response?.data?.message || 'Failed to fetch scoring rules';
        console.error('Fetch failed - Error message:', errorMsg);
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Error fetching rules (catch block):', err);
      console.error('Error response:', err.response);
      console.error('Error message:', err.message);

      let errorMessage = 'Failed to fetch scoring rules';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = () => {
    setEditingRule(null);
    setError(''); // Clear any previous errors
    setShowModal(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setShowModal(true);
  };

  const handleSaveRule = async (ruleData, setModalError) => {
    try {
      console.log('Saving rule data:', ruleData);
      const scoringRuleApi = new ScoringRuleApi();
      let response;
      if (editingRule) {
        // Update existing rule
        response = await scoringRuleApi.updateRule(editingRule.id, ruleData);
      } else {
        // Create new rule
        response = await scoringRuleApi.createRule(ruleData);
      }

      console.log('Save response:', response);
      console.log('Response data:', response?.data);

      if (response?.data?.success) {
        setSuccess(editingRule ? 'Rule updated successfully!' : 'Rule created successfully!');
        setShowModal(false);
        fetchAllRules();
        setTimeout(() => setSuccess(''), 3000);
        return { success: true };
      } else {
        // Return error to modal instead of setting in parent
        let errorMessage = 'Failed to save rule';

        // Try to get the most specific error message
        if (response?.data?.message) {
          errorMessage = response.data.message;
        } else if (response?.data?.error) {
          errorMessage = response.data.error;
        } else if (response?.data?.errors && Array.isArray(response.data.errors)) {
          errorMessage = response.data.errors.map(e => e.msg || e.message || e).join(', ');
        }

        console.error('Save failed with message:', errorMessage);

        if (setModalError) {
          setModalError(errorMessage);
        }
        return { success: false, message: errorMessage };
      }
    } catch (err) {
      console.error('Error saving rule (catch block):', err);
      console.error('Error response:', err.response);

      let errorMessage = 'Failed to save rule';

      // Try to extract error message from various sources
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        errorMessage = err.response.data.errors.map(e => e.msg || e.message || e).join(', ');
      } else if (err.message) {
        errorMessage = err.message;
      }

      console.error('Final error message:', errorMessage);

      if (setModalError) {
        setModalError(errorMessage);
      }
      return { success: false, message: errorMessage };
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      const scoringRuleApi = new ScoringRuleApi();
      const response = await scoringRuleApi.toggleStatus(rule.id, !rule.is_active);
      if (response?.data?.success) {
        setSuccess(`Rule ${rule.is_active ? 'deactivated' : 'activated'} successfully!`);
        fetchAllRules();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response?.data?.message || 'Failed to toggle rule');
      }
    } catch (err) {
      console.error('Error toggling rule:', err);
      setError('Failed to toggle rule');
    }
  };

  const handleDeleteClick = (rule) => {
    setRuleToDelete(rule);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!ruleToDelete) return;

    try {
      const scoringRuleApi = new ScoringRuleApi();
      const response = await scoringRuleApi.deleteRule(ruleToDelete.id);
      if (response?.data?.success) {
        setSuccess('Rule deleted successfully!');
        setShowDeleteConfirm(false);
        setRuleToDelete(null);
        fetchAllRules();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response?.data?.message || 'Failed to delete rule');
      }
    } catch (err) {
      console.error('Error deleting rule:', err);
      setError('Failed to delete rule');
    }
  };

  const getObjectLabel = (objectName) => {
    const obj = objects.find((o) => o.name === objectName);
    return obj?.label || objectName;
  };

  if (!allowViewScoringRule) {
    return null;
  }

  // Filter and search rules (only rules for allowed objects, then apply filters)
  const filteredRules = rulesForAllowedObjects.filter((rule) => {
    // Filter by object
    if (filterObject !== 'all' && rule.object_name !== filterObject) return false;

    // Filter by active status
    if (filterActive === 'active' && !rule.is_active) return false;
    if (filterActive === 'inactive' && rule.is_active) return false;

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        rule.description?.toLowerCase().includes(search) ||
        rule.object_name?.toLowerCase().includes(search) ||
        rule.score?.toString().includes(search)
      );
    }

    return true;
  });

  const getScoreBadgeVariant = (score) => {
    if (score > 0) return 'success';
    if (score < 0) return 'danger';
    return 'secondary';
  };

  const renderLogicSummary = (rule) => {
    if (!rule.config) return 'No config';

    let config = rule.config;
    if (typeof config === 'string') {
      try {
        config = JSON.parse(config);
      } catch (e) {
        return 'Invalid config';
      }
    }

    const logic = config.logic;
    if (!logic) return 'No conditions';

    if (logic.conditions && Array.isArray(logic.conditions)) {
      return `${logic.conditions.length} condition${logic.conditions.length !== 1 ? 's' : ''}`;
    }

    return 'No conditions';
  };

  const calculateTotalScore = (rule) => {
    if (!rule.config) return 0;

    let config = rule.config;
    if (typeof config === 'string') {
      try {
        config = JSON.parse(config);
      } catch (e) {
        return 0;
      }
    }

    const logic = config.logic;
    if (!logic || !logic.conditions) return 0;

    return logic.conditions.reduce((total, cond) => {
      const score = parseInt(cond.score) || 0;
      const operation = cond.scoreOperation || 'add';
      return total + (operation === 'subtract' ? -score : score);
    }, 0);
  };

  return (
    <Container fluid className="scoring-rule-manager">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>
                <FaStar className="me-2 text-warning" />
                Scoring Rule Manager
              </h2>
              <p className="text-muted">
                Create and manage scoring rules to automatically calculate scores for records
              </p>
            </div>
            {allowCreateScoringRule && (
              <Button
                variant="primary"
                onClick={handleCreateRule}
              >
                <FaPlus className="me-2" />
                New Scoring Rule
              </Button>
            )}
          </div>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Row className="mb-4">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Filter by Object</Form.Label>
            <Form.Select
              value={filterObject}
              onChange={(e) => setFilterObject(e.target.value)}
            >
              <option value="all">All Objects</option>
              {visibleObjects.map((obj) => (
                <option key={obj.name} value={obj.name}>
                  {obj.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group>
            <Form.Label>Filter by Status</Form.Label>
            <Form.Select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
            >
              <option value="all">All Rules</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group>
            <Form.Label>Search Rules</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search by description, object, or score..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Rules Table */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2">Loading scoring rules...</p>
            </div>
          ) : filteredRules.length === 0 ? (
            <div className="text-center py-5">
              <FaChartLine size={48} className="text-muted mb-3" />
              <h5>No Scoring Rules Found</h5>
              <p className="text-muted">
                {rulesForAllowedObjects.length === 0
                  ? (visibleObjects.length === 0
                    ? 'No objects are allowed for scoring rules in your plan.'
                    : 'Create your first scoring rule to get started')
                  : 'No rules match your current filters'}
              </p>
              {rulesForAllowedObjects.length === 0 && allowCreateScoringRule && visibleObjects.length > 0 && (
                <Button variant="primary" onClick={handleCreateRule} className="mt-3">
                  <FaPlus className="me-2" />
                  Create First Rule
                </Button>
              )}
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Object</th>
                  <th>Status</th>
                  <th>Description</th>
                  <th>Score</th>
                  <th>Logic</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRules.map(rule => (
                  <tr key={rule.id}>
                    <td>
                      <Badge bg="info">{getObjectLabel(rule.object_name)}</Badge>
                    </td>
                    <td>
                      <Badge bg={rule.is_active ? 'success' : 'secondary'}>
                        {rule.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      <div className="fw-bold">{rule.description || 'Untitled Rule'}</div>
                    </td>
                    <td>
                      <Badge bg={getScoreBadgeVariant(calculateTotalScore(rule))} className="px-3">
                        {calculateTotalScore(rule) > 0 ? '+' : ''}{calculateTotalScore(rule)}
                      </Badge>
                    </td>
                    <td>
                      <small className="text-muted">
                        {renderLogicSummary(rule)}
                      </small>
                    </td>
                    <td>
                      <small className="text-muted">
                        {new Date(rule.createddate).toLocaleDateString()}
                      </small>
                    </td>
                    <td>
                      {allowCreateScoringRule ? (
                        <div className="d-flex gap-2">
                          <OverlayTrigger overlay={<Tooltip>Edit Rule</Tooltip>}>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditRule(rule)}
                            >
                              <FaEdit />
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger overlay={<Tooltip>{rule.is_active ? 'Deactivate' : 'Activate'}</Tooltip>}>
                            <Button
                              variant={rule.is_active ? 'outline-warning' : 'outline-success'}
                              size="sm"
                              onClick={() => handleToggleRule(rule)}
                            >
                              {rule.is_active ? <FaToggleOn /> : <FaToggleOff />}
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger overlay={<Tooltip>Delete Rule</Tooltip>}>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteClick(rule)}
                            >
                              <FaTrash />
                            </Button>
                          </OverlayTrigger>
                        </div>
                      ) : (
                        <span className="text-muted small">View only</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Create/Edit Modal */}
      {showModal && (
        <ScoringRuleForm
          show={showModal}
          onHide={() => setShowModal(false)}
          onSave={handleSaveRule}
          editingRule={editingRule}
          objects={visibleObjects}
          existingRules={rulesForAllowedObjects}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the rule <strong>"{ruleToDelete?.description}"</strong>?
          <br />
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ScoringRuleManager;
